/**
 * Guided Meditations Component
 * 
 * Pre-programmed guided meditation sessions with audio cues
 * 
 * REFACTORED: Now uses proper context integration with mathematically explicit calculations
 * - useSacredAudio hook for audio generation (properly integrated with AudioContext)
 * - useMeditation context for session tracking
 * - useAudio context for audio state management
 * - All frequency calculations are mathematically explicit
 * - All timing calculations are precise and spelled out
 */

import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, SkipForward, Volume2 } from 'lucide-react';
import { useSacredAudio } from '../hooks/useSacredAudio';
import { MeditationContext } from '../contexts/MeditationContext';
import { AudioContext as AudioContextProvider } from '../contexts/AudioContext';
import { audioManager } from '../core/audio/AudioManagerAdapter';
import toast from 'react-hot-toast';

interface Meditation {
  id: string;
  title: string;
  description: string;
  duration: number; // minutes
  steps: MeditationStep[];
  frequency?: number; // Background tone frequency
}

interface MeditationStep {
  text: string;
  duration: number; // seconds
  soundCue?: 'bell' | 'tone' | 'silence';
  frequency?: number; // Optional: step-specific frequency (for chakra meditation)
}

interface ChakraStepDefinition {
  name: string;
  frequency: number;
  color: string;
  mantra: string;
  guidance: string;
}

interface MeditationVisualStyle {
  accent: string;
  gradient: string;
  halo: string;
  tileShape: string;
}

// Root-to-crown chakra sequence used across the full chakra activation meditation.
const CHAKRA_ACTIVATION_SEQUENCE: ChakraStepDefinition[] = [
  { name: 'Root Chakra (Muladhara)', frequency: 396, color: 'red', mantra: 'LAM', guidance: 'Feel grounded, stable, and safe at the base of your spine.' },
  { name: 'Sacral Chakra (Svadhisthana)', frequency: 417, color: 'orange', mantra: 'VAM', guidance: 'Feel creative flow and emotional ease in your lower abdomen.' },
  { name: 'Solar Plexus Chakra (Manipura)', frequency: 528, color: 'yellow', mantra: 'RAM', guidance: 'Feel confidence, willpower, and radiant strength at your core.' },
  { name: 'Heart Chakra (Anahata)', frequency: 639, color: 'green', mantra: 'YAM', guidance: 'Feel love, compassion, and connection in the center of your chest.' },
  { name: 'Throat Chakra (Vishuddha)', frequency: 741, color: 'blue', mantra: 'HAM', guidance: 'Feel truthful expression and clear communication at your throat.' },
  { name: 'Third Eye Chakra (Ajna)', frequency: 852, color: 'indigo', mantra: 'OM', guidance: 'Feel insight, intuition, and inner vision between your brows.' },
  { name: 'Crown Chakra (Sahasrara)', frequency: 963, color: 'violet', mantra: 'AH', guidance: 'Feel unity, stillness, and spacious awareness at the top of your head.' }
];

const buildChakraActivationSteps = (durationMinutes: number): MeditationStep[] => {
  const totalDurationSeconds = Math.max(60, Math.round(durationMinutes * 60));
  const chakraCount = CHAKRA_ACTIVATION_SEQUENCE.length;
  const secondsPerChakra = Math.floor(totalDurationSeconds / chakraCount);
  const remainderSeconds = totalDurationSeconds - (secondsPerChakra * chakraCount);

  return CHAKRA_ACTIVATION_SEQUENCE.map((chakra, index) => {
    const duration = secondsPerChakra + (index < remainderSeconds ? 1 : 0);
    return {
      text: `${chakra.name}: Visualize ${chakra.color} light. Silently repeat ${chakra.mantra}. ${chakra.guidance}`,
      duration,
      frequency: chakra.frequency,
      soundCue: index === 0 ? 'bell' : undefined
    };
  });
};

// Keep sub-bass octave-related to the active meditation tone while staying in a meditative low range.
const calculateSubBassFrequency = (baseFrequency: number): number => {
  let subBassFrequency = baseFrequency;

  while (subBassFrequency > 110) {
    subBassFrequency /= 2;
  }

  while (subBassFrequency < 40) {
    subBassFrequency *= 2;
  }

  return subBassFrequency;
};

const guidedMeditations: Meditation[] = [
  {
    id: 'breath-awareness',
    title: 'Breath Awareness',
    description: 'Simple mindfulness meditation focusing on the natural rhythm of breath',
    duration: 10,
    frequency: 432, // A=432Hz
    steps: [
      {
        text: 'Find a comfortable seated position. Close your eyes gently.',
        duration: 20,
        soundCue: 'bell'
      },
      {
        text: 'Begin to notice your natural breath. No need to change it.',
        duration: 30
      },
      {
        text: 'Feel the sensation of air entering your nostrils. Cool and refreshing.',
        duration: 40
      },
      {
        text: 'Notice the pause at the top of the inhale.',
        duration: 30
      },
      {
        text: 'Feel the warm air leaving as you exhale. Release and let go.',
        duration: 40
      },
      {
        text: 'When your mind wanders, gently return to the breath. This is natural.',
        duration: 45
      },
      {
        text: 'Continue breathing naturally, observing each breath with curiosity.',
        duration: 180
      },
      {
        text: 'Begin to deepen your breath. Inhale deeply through your nose.',
        duration: 30
      },
      {
        text: 'Exhale slowly through your mouth. Release any tension.',
        duration: 30
      },
      {
        text: 'Gently open your eyes. Notice how you feel. You have meditated.',
        duration: 25,
        soundCue: 'bell'
      }
    ]
  },
  {
    id: 'chakra-activation',
    title: 'Chakra Activation',
    description: 'Root-to-crown chakra resonance sequence with one continuous ordered frequency journey',
    duration: 14,
    steps: buildChakraActivationSteps(14)
  },
  {
    id: 'golden-light',
    title: 'Golden Light Meditation',
    description: 'Fill your being with healing golden light',
    duration: 8,
    frequency: 528, // Love frequency
    steps: [
      {
        text: 'Close your eyes and take three deep breaths.',
        duration: 20,
        soundCue: 'bell'
      },
      {
        text: 'Imagine a sphere of golden light above your head.',
        duration: 30
      },
      {
        text: 'This light is pure love, healing, and divine energy.',
        duration: 30
      },
      {
        text: 'As you inhale, draw this golden light into your crown.',
        duration: 40
      },
      {
        text: 'Feel it filling your head, your brain, your face.',
        duration: 45
      },
      {
        text: 'The light flows down into your neck and shoulders.',
        duration: 45
      },
      {
        text: 'Golden light fills your chest, your heart, your lungs.',
        duration: 45
      },
      {
        text: 'It flows into your arms, your hands, your fingers.',
        duration: 40
      },
      {
        text: 'Golden light fills your abdomen, your organs, your back.',
        duration: 45
      },
      {
        text: 'It flows into your hips, your legs, your feet.',
        duration: 40
      },
      {
        text: 'Your entire body glows with golden healing light.',
        duration: 60
      },
      {
        text: 'This light expands beyond your body, creating a protective sphere.',
        duration: 40
      },
      {
        text: 'Take a moment to rest in this golden light.',
        duration: 60
      },
      {
        text: 'Gently open your eyes, carrying this light with you.',
        duration: 20,
        soundCue: 'bell'
      }
    ]
  },
  {
    id: 'void-meditation',
    title: 'Void Meditation',
    description: 'Deep bass-forward drone for resting in infinite spacious emptiness',
    duration: 12,
    frequency: 108, // Low drone carrier for void practice
    steps: [
      {
        text: 'Find stillness in your body. Close your eyes.',
        duration: 20,
        soundCue: 'bell'
      },
      {
        text: 'Imagine you are floating in infinite dark space.',
        duration: 40
      },
      {
        text: 'There is no up or down. No beginning or end.',
        duration: 40
      },
      {
        text: 'You are surrounded by the void - pure potential.',
        duration: 40
      },
      {
        text: 'This emptiness is not empty. It is pregnant with possibility.',
        duration: 50
      },
      {
        text: 'All things arise from this void and return to it.',
        duration: 50
      },
      {
        text: 'Your thoughts arise and dissolve in this space.',
        duration: 60
      },
      {
        text: 'Your emotions arise and dissolve in this space.',
        duration: 60
      },
      {
        text: 'Even your sense of self arises and dissolves.',
        duration: 60
      },
      {
        text: 'Rest in the void. Nothing to do. Nowhere to go.',
        duration: 180
      },
      {
        text: 'Slowly, begin to return. Bring the void with you.',
        duration: 40
      },
      {
        text: 'The void is always here, beneath everything.',
        duration: 40
      },
      {
        text: 'Open your eyes gently. You carry the void within.',
        duration: 20,
        soundCue: 'bell'
      }
    ]
  }
];

const MEDITATION_VISUAL_STYLES: Record<string, MeditationVisualStyle> = {
  'breath-awareness': {
    accent: '#14b8a6',
    gradient: 'linear-gradient(145deg, rgba(15, 118, 110, 0.32) 0%, rgba(12, 74, 110, 0.28) 100%)',
    halo: 'rgba(20, 184, 166, 0.35)',
    tileShape: '26px 18px 26px 18px'
  },
  'chakra-activation': {
    accent: '#f97316',
    gradient: 'linear-gradient(145deg, rgba(249, 115, 22, 0.26) 0%, rgba(190, 24, 93, 0.22) 100%)',
    halo: 'rgba(249, 115, 22, 0.35)',
    tileShape: '20px 28px 20px 28px'
  },
  'golden-light': {
    accent: '#f59e0b',
    gradient: 'linear-gradient(145deg, rgba(245, 158, 11, 0.3) 0%, rgba(251, 191, 36, 0.22) 100%)',
    halo: 'rgba(245, 158, 11, 0.36)',
    tileShape: '28px 16px 28px 16px'
  },
  'void-meditation': {
    accent: '#0ea5e9',
    gradient: 'linear-gradient(145deg, rgba(14, 116, 144, 0.32) 0%, rgba(15, 23, 42, 0.3) 100%)',
    halo: 'rgba(14, 165, 233, 0.32)',
    tileShape: '16px 26px 16px 26px'
  }
};

const DEFAULT_MEDITATION_VISUAL_STYLE: MeditationVisualStyle = {
  accent: '#14b8a6',
  gradient: 'linear-gradient(145deg, rgba(15, 118, 110, 0.24) 0%, rgba(15, 23, 42, 0.24) 100%)',
  halo: 'rgba(20, 184, 166, 0.3)',
  tileShape: '24px'
};

/**
 * Calculate total meditation duration in seconds
 * Formula: Sum of all step durations
 */
const calculateTotalDuration = (steps: MeditationStep[]): number => {
  return steps.reduce((total: number, step: MeditationStep) => {
    return total + step.duration;
  }, 0);
};

/**
 * Calculate background tone volume for meditation
 * Formula: Base volume (0.15) adjusted by meditation type
 * Volume range: 0.1 (min) to 0.25 (max) for background tones
 */
const calculateBackgroundVolume = (meditationType: string): number => {
  const baseVolume = 0.15;
  const volumeAdjustments: { [key: string]: number } = {
    'breath-awareness': 0.0,      // No adjustment
    'chakra-activation': 0.05,    // Slightly louder for chakra work
    'golden-light': 0.05,         // Slightly louder for healing
    'void-meditation': 0.03       // Fuller body for low drone practice
  };
  const adjustment = volumeAdjustments[meditationType] || 0.0;
  const calculatedVolume = baseVolume + adjustment;
  // Clamp to valid range [0.1, 0.25]
  return Math.max(0.1, Math.min(0.25, calculatedVolume));
};

/**
 * Calculate fade times for background tone
 * Formula: Fade time = 3 seconds (standard) or 10% of total duration, whichever is smaller
 */
const calculateFadeTimes = (totalDurationSeconds: number): { fadeIn: number; fadeOut: number } => {
  const standardFadeTime = 3.0; // seconds
  const percentageBasedFade = totalDurationSeconds * 0.1; // 10% of total duration
  const fadeTime = Math.min(standardFadeTime, percentageBasedFade);
  return {
    fadeIn: fadeTime,
    fadeOut: fadeTime
  };
};

/**
 * Calculate bell tone frequency based on step type
 * Formula: Base frequency (528 Hz) with optional modulation
 */
const calculateBellFrequency = (stepType: 'bell' | 'tone' | 'silence' | undefined): number => {
  if (stepType === 'bell') {
    // 528 Hz - Solfeggio MI frequency (Love/Transformation)
    return 528.0;
  } else if (stepType === 'tone') {
    // 432 Hz - Natural tuning frequency (A4)
    return 432.0;
  }
  return 0; // Silence
};

/**
 * Calculate bell tone duration
 * Formula: Base duration (2 seconds) with fade times
 */
const calculateBellDuration = (): number => {
  const baseDuration = 2.0; // seconds
  const fadeOutTime = 0.5; // seconds
  return baseDuration + fadeOutTime;
};

const GuidedMeditations: React.FC = () => {
  const [selectedMeditation, setSelectedMeditation] = useState<Meditation | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  // Audio context integration
  const {
    playSacredTone,
    playBreathAwarenessTone,
    playGoldenLightTone,
    stopAudio,
    initializeAudio,
    isInitialized: isAudioInitialized
  } = useSacredAudio();
  
  // Meditation context integration (optional - graceful degradation if provider not available)
  // Use useContext directly to check if provider exists without throwing
  // Formula: If context is undefined, provider is not available - use fallback functions
  const meditationContextValue = useContext(MeditationContext);
  
  // Extract context values with fallbacks if context not available
  // Formula: If contextValue exists, use its methods; otherwise use no-op functions
  const startSession = meditationContextValue?.startSession || (() => {
    console.log('ℹ️ MeditationContext not available - session tracking disabled');
  });
  const endSession = meditationContextValue?.endSession || (() => {
    // No-op: context not available
  });
  const currentSession = meditationContextValue?.currentSession || null;
  
  // Audio context state (optional - graceful degradation if provider not available)
  // Use useContext directly to check if provider exists without throwing
  // Formula: If context is undefined, provider is not available - use fallback values
  const audioContextValue = useContext(AudioContextProvider);
  
  // Fallback audio initialization function (memoized for stability)
  // Formula: If AudioContext not available, use audioManager directly
  const fallbackInitializeAudio = useCallback(async () => {
    console.log('ℹ️ AudioContext not available - using direct audio initialization');
    // Fallback: use audioManager directly if context not available
    // Formula: audioManager.initialize() takes no arguments - it handles user interaction internally
    if (audioManager.isReady()) {
      return;
    }
    try {
      await audioManager.initialize();
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  }, []);
  
  // Extract context values with fallbacks if context not available
  // Formula: If contextValue exists, use its properties; otherwise use default values
  const isAudioContextInitialized = audioContextValue?.isInitialized || false;
  const initializeAudioContext = audioContextValue?.initialize || fallbackInitializeAudio;
  
  // Refs for managing background tone and step timers
  const backgroundToneTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stepTimerRef = useRef<NodeJS.Timeout | null>(null);
  const bellTimerRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundOscillatorRef = useRef<OscillatorNode | null>(null);
  const backgroundGainRef = useRef<GainNode | null>(null);
  const subBassOscillatorRef = useRef<OscillatorNode | null>(null);
  const subBassGainRef = useRef<GainNode | null>(null);
  const lfoOscillatorRef = useRef<OscillatorNode | null>(null);
  const lfoGainRef = useRef<GainNode | null>(null);
  const previousChakraFrequencyRef = useRef<number | null>(null);

  const stopActiveTones = useCallback((stopOscillators: boolean = true): void => {
    if (stopOscillators && backgroundOscillatorRef.current) {
      try {
        backgroundOscillatorRef.current.stop();
      } catch (e) {
        // Already stopped
      }
    }

    if (stopOscillators && subBassOscillatorRef.current) {
      try {
        subBassOscillatorRef.current.stop();
      } catch (e) {
        // Already stopped
      }
    }

    if (stopOscillators && lfoOscillatorRef.current) {
      try {
        lfoOscillatorRef.current.stop();
      } catch (e) {
        // Already stopped
      }
    }

    if (backgroundOscillatorRef.current) {
      try {
        backgroundOscillatorRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      backgroundOscillatorRef.current = null;
    }

    if (subBassOscillatorRef.current) {
      try {
        subBassOscillatorRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      subBassOscillatorRef.current = null;
    }

    if (lfoOscillatorRef.current) {
      try {
        lfoOscillatorRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      lfoOscillatorRef.current = null;
    }

    if (backgroundGainRef.current) {
      try {
        backgroundGainRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      backgroundGainRef.current = null;
    }

    if (subBassGainRef.current) {
      try {
        subBassGainRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      subBassGainRef.current = null;
    }

    if (lfoGainRef.current) {
      try {
        lfoGainRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      lfoGainRef.current = null;
    }
  }, []);

  /**
   * Play continuous background tone with mathematically explicit calculations
   * 
   * Formula for gain envelope:
   * - Fade in: linear ramp from 0 to targetVolume over fadeInTime
   * - Sustain: hold at targetVolume for (totalDuration - fadeInTime - fadeOutTime)
   * - Fade out: linear ramp from targetVolume to 0 over fadeOutTime
   * 
   * Formula for frequency:
   * - Base frequency from meditation data
   * - Optional gentle LFO modulation: frequency ± (frequency * modulationDepth)
   * - Modulation rate: typically 0.1 Hz (10 second period)
   */
  const playContinuousBackgroundTone = useCallback((
    meditationId: string,
    frequency: number,
    totalDurationSeconds: number,
    volume: number,
    fadeInSeconds: number,
    fadeOutSeconds: number
  ): void => {
    // Get audio context from AudioManager
    const audioContext = audioManager.getMainAudioContext();
    const masterGain = audioManager.getMainMasterGain();

    if (!audioContext || !masterGain) {
      console.warn('⚠️ Audio context not available for background tone');
      return;
    }

    // Stop any existing background tone before starting a new one.
    stopActiveTones(true);

    const currentTime = audioContext.currentTime;

    // Create oscillator for continuous tone
    // Formula: Oscillator frequency = base frequency (from meditation data)
    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sine'; // Pure sine wave for meditation
    oscillator.frequency.value = frequency;
    const isVoidMeditation = meditationId === 'void-meditation';
    const subBassFrequency = isVoidMeditation
      ? Math.max(36, calculateSubBassFrequency(frequency) * 0.5)
      : calculateSubBassFrequency(frequency);
    const subBassOscillator = audioContext.createOscillator();
    subBassOscillator.type = 'sine';
    subBassOscillator.frequency.value = subBassFrequency;

    // Add gentle frequency modulation for organic feel
    // Formula: LFO frequency = 0.1 Hz (10 second period)
    // Formula: Modulation depth = 1% of base frequency
    const lfoFrequency = isVoidMeditation ? 0.04 : 0.1; // slower drift for void drone
    const modulationDepth = frequency * (isVoidMeditation ? 0.004 : 0.01);
    
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = lfoFrequency;
    lfoGain.gain.value = modulationDepth;
    
    lfo.connect(lfoGain);
    lfoGain.connect(oscillator.frequency);

    // Create gain node for volume envelope
    const gainNode = audioContext.createGain();
    const subBassGainNode = audioContext.createGain();
    const subBassVolume = volume;
    
    // Calculate envelope timing
    // Formula: sustainStart = fadeInTime
    // Formula: sustainEnd = totalDuration - fadeOutTime
    const sustainStartTime = fadeInSeconds;
    const sustainEndTime = totalDurationSeconds - fadeOutSeconds;
    const sustainDuration = sustainEndTime - sustainStartTime;

    // Apply ADSR envelope with explicit mathematical calculations
    // Formula: gain(t) = {
    //   0 to fadeIn: linear ramp from 0 to volume
    //   fadeIn to (totalDuration - fadeOut): constant at volume
    //   (totalDuration - fadeOut) to totalDuration: linear ramp from volume to 0
    // }
    
    // Initial gain: 0
    gainNode.gain.setValueAtTime(0, currentTime);
    subBassGainNode.gain.setValueAtTime(0, currentTime);
    
    // Fade in: linear ramp from 0 to target volume
    // Formula: gain(t) = (volume / fadeInTime) * t, for t in [0, fadeInTime]
    gainNode.gain.linearRampToValueAtTime(volume, currentTime + fadeInSeconds);
    subBassGainNode.gain.linearRampToValueAtTime(subBassVolume, currentTime + fadeInSeconds);
    
    // Sustain: hold at target volume
    // Formula: gain(t) = volume, for t in [fadeInTime, totalDuration - fadeOutTime]
    gainNode.gain.setValueAtTime(volume, currentTime + sustainEndTime);
    subBassGainNode.gain.setValueAtTime(subBassVolume, currentTime + sustainEndTime);
    
    // Fade out: linear ramp from target volume to 0
    // Formula: gain(t) = volume - (volume / fadeOutTime) * (t - (totalDuration - fadeOutTime)), 
    //           for t in [totalDuration - fadeOutTime, totalDuration]
    gainNode.gain.linearRampToValueAtTime(0, currentTime + totalDurationSeconds);
    subBassGainNode.gain.linearRampToValueAtTime(0, currentTime + totalDurationSeconds);

    // Connect audio graph: LFO → Oscillator Frequency
    //                    Oscillator → Gain (envelope) → Master Gain → Destination
    oscillator.connect(gainNode);
    gainNode.connect(masterGain);
    subBassOscillator.connect(subBassGainNode);
    subBassGainNode.connect(masterGain);

    // Start oscillators
    lfo.start(currentTime);
    oscillator.start(currentTime);
    subBassOscillator.start(currentTime);
    
    // Stop oscillators at end of duration
    lfo.stop(currentTime + totalDurationSeconds);
    oscillator.stop(currentTime + totalDurationSeconds);
    subBassOscillator.stop(currentTime + totalDurationSeconds);

    // Store references for cleanup
    backgroundOscillatorRef.current = oscillator;
    backgroundGainRef.current = gainNode;
    subBassOscillatorRef.current = subBassOscillator;
    subBassGainRef.current = subBassGainNode;
    lfoOscillatorRef.current = lfo;
    lfoGainRef.current = lfoGain;

    // Cleanup on oscillator end
    oscillator.onended = () => {
      try {
        lfo.stop();
        lfo.disconnect();
        lfoGain.disconnect();
        oscillator.disconnect();
        gainNode.disconnect();
        subBassOscillator.disconnect();
        subBassGainNode.disconnect();
      } catch (e) {
        // Already cleaned up
      }
      if (backgroundOscillatorRef.current === oscillator) {
        backgroundOscillatorRef.current = null;
      }
      if (backgroundGainRef.current === gainNode) {
        backgroundGainRef.current = null;
      }
      if (subBassOscillatorRef.current === subBassOscillator) {
        subBassOscillatorRef.current = null;
      }
      if (subBassGainRef.current === subBassGainNode) {
        subBassGainRef.current = null;
      }
      if (lfoOscillatorRef.current === lfo) {
        lfoOscillatorRef.current = null;
      }
      if (lfoGainRef.current === lfoGain) {
        lfoGainRef.current = null;
      }
    };

    console.log(
      `🎵 ${meditationId} tone: ${frequency.toFixed(2)}Hz + sub-bass ${subBassFrequency.toFixed(2)}Hz for ${totalDurationSeconds.toFixed(2)}s`
    );
    console.log(`   Volume: ${(volume * 100).toFixed(1)}% | Fade in: ${fadeInSeconds.toFixed(2)}s | Fade out: ${fadeOutSeconds.toFixed(2)}s`);
  }, [stopActiveTones]);

  /**
   * Play chakra-specific tone for a meditation step
   * 
   * Formula for smooth transitions:
   * - Fade out previous tone over 2 seconds
   * - Fade in new tone over 2 seconds
   * - Sustain new tone for (stepDuration - fadeInTime - fadeOutTime)
   * 
   * Formula for volume:
   * - Base volume: 0.15 (15%)
   * - Chakra meditation adjustment: +0.05 (20% total)
   */
  const playChakraStepTone = useCallback((
    frequency: number,
    stepDurationSeconds: number,
    previousFrequency: number | null = null
  ): void => {
    // Get audio context from AudioManager
    const audioContext = audioManager.getMainAudioContext();
    const masterGain = audioManager.getMainMasterGain();

    if (!audioContext || !masterGain) {
      console.warn('⚠️ Audio context not available for chakra tone');
      return;
    }

    const currentTime = audioContext.currentTime;
    const fadeTime = 2.0; // seconds for smooth transition
    const mainToneVolume = 0.14; // main tone level for chakra steps
    const subBassVolume = mainToneVolume; // match sub-bass level to main tone level
    const subBassFrequency = calculateSubBassFrequency(frequency);

    // If there's a previous tone, fade it out smoothly (crossfade with new tone)
    if (backgroundOscillatorRef.current && previousFrequency !== null) {
      const previousGain = backgroundGainRef.current;
      if (previousGain) {
        // Fade out previous tone over fadeTime (crossfade with new tone)
        previousGain.gain.linearRampToValueAtTime(0, currentTime + fadeTime);
      }
      const previousSubBassGain = subBassGainRef.current;
      if (previousSubBassGain) {
        previousSubBassGain.gain.linearRampToValueAtTime(0, currentTime + fadeTime);
      }
    } else {
      // Stop any existing background tone immediately if no previous frequency
      stopActiveTones(true);
    }

    // Calculate timing for new tone
    // Formula: Start new tone immediately (with crossfade if transitioning)
    // New tone plays for full step duration
    const startTime = currentTime;
    const sustainStartTime = startTime + fadeTime;
    const sustainEndTime = startTime + stepDurationSeconds - fadeTime;
    const sustainDuration = Math.max(0, sustainEndTime - sustainStartTime);

    // Create oscillator for chakra tone
    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sine'; // Pure sine wave for chakra meditation
    oscillator.frequency.value = frequency;

    const subBassOscillator = audioContext.createOscillator();
    subBassOscillator.type = 'sine';
    subBassOscillator.frequency.value = subBassFrequency;

    // Add gentle frequency modulation for organic feel
    const lfoFrequency = 0.1; // Hz - very slow modulation
    const modulationDepth = frequency * 0.01; // 1% of base frequency
    
    const lfo = audioContext.createOscillator();
    const lfoGain = audioContext.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = lfoFrequency;
    lfoGain.gain.value = modulationDepth;
    
    lfo.connect(lfoGain);
    lfoGain.connect(oscillator.frequency);

    // Create gain node for volume envelope
    const gainNode = audioContext.createGain();
    const subBassGainNode = audioContext.createGain();
    
    // Apply ADSR envelope with smooth transitions
    // Initial gain: 0
    gainNode.gain.setValueAtTime(0, startTime);
    
    // Fade in: linear ramp from 0 to target volume
    gainNode.gain.linearRampToValueAtTime(mainToneVolume, sustainStartTime);
    
    // Sustain: hold at target volume
    gainNode.gain.setValueAtTime(mainToneVolume, sustainEndTime);
    
    // Fade out: linear ramp from target volume to 0
    gainNode.gain.linearRampToValueAtTime(0, startTime + stepDurationSeconds);

    subBassGainNode.gain.setValueAtTime(0, startTime);
    subBassGainNode.gain.linearRampToValueAtTime(subBassVolume, sustainStartTime);
    subBassGainNode.gain.setValueAtTime(subBassVolume, sustainEndTime);
    subBassGainNode.gain.linearRampToValueAtTime(0, startTime + stepDurationSeconds);

    // Connect audio graph
    oscillator.connect(gainNode);
    gainNode.connect(masterGain);
    subBassOscillator.connect(subBassGainNode);
    subBassGainNode.connect(masterGain);

    // Start oscillators
    lfo.start(startTime);
    oscillator.start(startTime);
    subBassOscillator.start(startTime);
    
    // Stop oscillators at end of step duration
    const stopTime = startTime + stepDurationSeconds;
    lfo.stop(stopTime);
    oscillator.stop(stopTime);
    subBassOscillator.stop(stopTime);

    // Store references for cleanup
    backgroundOscillatorRef.current = oscillator;
    backgroundGainRef.current = gainNode;
    subBassOscillatorRef.current = subBassOscillator;
    subBassGainRef.current = subBassGainNode;
    lfoOscillatorRef.current = lfo;
    lfoGainRef.current = lfoGain;

    // Cleanup on oscillator end
    oscillator.onended = () => {
      try {
        lfo.stop();
        lfo.disconnect();
        lfoGain.disconnect();
        oscillator.disconnect();
        gainNode.disconnect();
        subBassOscillator.disconnect();
        subBassGainNode.disconnect();
      } catch (e) {
        // Already cleaned up
      }
      if (backgroundOscillatorRef.current === oscillator) {
        backgroundOscillatorRef.current = null;
      }
      if (backgroundGainRef.current === gainNode) {
        backgroundGainRef.current = null;
      }
      if (subBassOscillatorRef.current === subBassOscillator) {
        subBassOscillatorRef.current = null;
      }
      if (subBassGainRef.current === subBassGainNode) {
        subBassGainRef.current = null;
      }
      if (lfoOscillatorRef.current === lfo) {
        lfoOscillatorRef.current = null;
      }
      if (lfoGainRef.current === lfoGain) {
        lfoGainRef.current = null;
      }
    };

    console.log(
      `🎵 Chakra tone: ${frequency.toFixed(2)}Hz + sub-bass ${subBassFrequency.toFixed(2)}Hz for ${stepDurationSeconds.toFixed(2)}s`
    );
  }, [stopActiveTones]);

  /**
   * Handle meditation step progression with precise timing
   * Formula: Each step runs for its exact duration in seconds
   */
  useEffect(() => {
    // Clear any existing step timer
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }

    if (!isPlaying || !selectedMeditation) {
      return;
    }

    const currentStep = selectedMeditation.steps[currentStepIndex];
    if (!currentStep) {
      // Meditation complete - all steps finished
      handleComplete();
      return;
    }

    // Set initial time remaining for this step
    setTimeRemaining(currentStep.duration);

    // Play sound cue for this step if specified
    if (isAudioInitialized && currentStep.soundCue) {
      const bellFrequency = calculateBellFrequency(currentStep.soundCue);
      if (bellFrequency > 0) {
        const bellDuration = calculateBellDuration();
        // Play bell tone with explicit frequency and duration
        playSacredTone(bellFrequency, bellDuration, 'sine');
      }
    }

    // Play chakra-specific tone if this step has a frequency (for chakra meditation)
    if (isAudioInitialized && currentStep.frequency && selectedMeditation.id === 'chakra-activation') {
      const previousFrequency = previousChakraFrequencyRef.current;
      playChakraStepTone(
        currentStep.frequency,
        currentStep.duration,
        previousFrequency
      );
      previousChakraFrequencyRef.current = currentStep.frequency;
    } else if (selectedMeditation.id === 'chakra-activation' && !currentStep.frequency) {
      // Stop any playing chakra tone when step doesn't have a frequency (e.g., intro/outro)
      stopActiveTones(true);
      previousChakraFrequencyRef.current = null;
    }

    // Create precise interval timer: 1000ms = 1 second
    const intervalDuration = 1000; // milliseconds
    stepTimerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1; // Decrement by exactly 1 second
        if (newTime <= 0) {
          // Step complete - move to next step
          if (currentStepIndex < selectedMeditation.steps.length - 1) {
            setCurrentStepIndex(prevIndex => prevIndex + 1);
          } else {
            // All steps complete
            handleComplete();
          }
          return 0;
        }
        return newTime;
      });
    }, intervalDuration);

    // Cleanup: clear interval when component unmounts or dependencies change
    return () => {
      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
        stepTimerRef.current = null;
      }
    };
  }, [isPlaying, currentStepIndex, selectedMeditation, isAudioInitialized, playSacredTone, playChakraStepTone, stopActiveTones]);

  /**
   * Play background tone when meditation starts
   * Formula: Total duration = sum of all step durations
   * Volume = calculated based on meditation type
   * Fade times = calculated based on total duration
   * 
   * Special handling for specialized meditations:
   * - Breath Awareness: Uses playBreathAwarenessTone with grounding frequency balance
   * - Golden Light: Uses playGoldenLightTone with warm, healing envelope
   */
  useEffect(() => {
    // Clear any existing background tone timer
    if (backgroundToneTimerRef.current) {
      clearTimeout(backgroundToneTimerRef.current);
      backgroundToneTimerRef.current = null;
    }

    // Skip background tone for chakra meditation (handled step-by-step)
    if (selectedMeditation?.id === 'chakra-activation') {
      return;
    }

    if (!isPlaying || !selectedMeditation?.frequency || !isAudioInitialized) {
      // Stop background tone if meditation is paused or stopped
      stopActiveTones(true);
      return;
    }

    // Calculate total duration: sum of all step durations in seconds
    // Formula: totalDuration = Σ(step.duration) for all steps
    const totalDurationSeconds = calculateTotalDuration(selectedMeditation.steps);
    
    // SPECIAL HANDLING: Breath Awareness uses specialized tone
    // Uses balanced frequency stack (432 Hz primary + 216 Hz + 108 Hz sub-bass)
    // ADSR optimized for centering: 1.2s attack, 0.8 sustain, 0.6s release
    // Prevents high frequencies from overpowering lows
    if (selectedMeditation.id === 'breath-awareness') {
      playBreathAwarenessTone(totalDurationSeconds);
      return;
    }
    
    // SPECIAL HANDLING: Golden Light meditation uses specialized tone
    // Uses extended ADSR envelope for warmth (2s attack, 0.85 sustain, 1s release)
    // With harmonic undertones and warm filtering for healing quality
    if (selectedMeditation.id === 'golden-light') {
      // Play golden light special tone with optimized warmth envelope
      // Duration: total meditation time (naturally handles all passages)
      playGoldenLightTone(totalDurationSeconds);
      return;
    }
    
    // STANDARD HANDLING: Other meditations use regular background tone
    // Calculate background volume: base volume adjusted by meditation type
    // Formula: volume = baseVolume + adjustment, clamped to [0.1, 0.25]
    const backgroundVolume = calculateBackgroundVolume(selectedMeditation.id);
    
    // Calculate fade times: standard 3 seconds or 10% of duration, whichever is smaller
    // Formula: fadeTime = min(3.0, totalDuration * 0.1)
    const { fadeIn, fadeOut } = calculateFadeTimes(totalDurationSeconds);
    
    // Get meditation frequency (explicitly defined in meditation data)
    // This is the base frequency for the background tone
    const meditationFrequency = selectedMeditation.frequency;
    
    // Play continuous background tone with mathematically explicit calculations
    playContinuousBackgroundTone(
      selectedMeditation.id,
      meditationFrequency,
      totalDurationSeconds,
      backgroundVolume,
      fadeIn,
      fadeOut
    );
    
  }, [isPlaying, selectedMeditation, isAudioInitialized, playContinuousBackgroundTone, playBreathAwarenessTone, playGoldenLightTone, stopActiveTones]);

  /**
   * Initialize audio system if needed
   * Formula: Check if audio is initialized, if not, initialize it
   */
  const ensureAudioInitialized = useCallback(async (): Promise<boolean> => {
    if (!isAudioInitialized) {
      try {
        await initializeAudio();
        // Also ensure AudioContext is initialized
        if (!isAudioContextInitialized) {
          await initializeAudioContext();
        }
        return true;
      } catch (error) {
        console.error('Failed to initialize audio:', error);
        toast.error('Failed to initialize audio. Please try again.');
        return false;
      }
    }
    return true;
  }, [isAudioInitialized, isAudioContextInitialized, initializeAudio, initializeAudioContext]);

  /**
   * Start meditation session
   * Formula: Initialize audio → Start session → Begin playback
   */
  const handleStart = useCallback(async (meditation: Meditation) => {
    // Ensure audio is initialized before starting
    const audioReady = await ensureAudioInitialized();
    if (!audioReady) {
      return;
    }

    // Calculate total duration for session tracking
    const totalDurationSeconds = calculateTotalDuration(meditation.steps);
    const totalDurationMinutes = Math.ceil(totalDurationSeconds / 60);

    // Start meditation session in context
    startSession(meditation.id, totalDurationMinutes);

    // Set meditation state
    setSelectedMeditation(meditation);
    setCurrentStepIndex(0);
    setIsPlaying(true);
    
    toast.success(`Starting: ${meditation.title}`);
  }, [ensureAudioInitialized, startSession]);

  /**
   * Pause meditation
   * Formula: Stop playback → Stop audio → Keep state for resume
   */
  const handlePause = useCallback(() => {
    setIsPlaying(false);
    // Stop all audio playback
    stopAudio();
  }, [stopAudio]);

  /**
   * Resume meditation
   * Formula: Resume playback → Continue from current step
   */
  const handleResume = useCallback(() => {
    setIsPlaying(true);
    // Audio will resume automatically when isPlaying becomes true
  }, []);

  /**
   * Stop meditation
   * Formula: Stop playback → Clear state → End session
   */
  const handleStop = useCallback(() => {
    setIsPlaying(false);
    
    // Stop all audio (including short tones)
    stopAudio();
    
    // Stop background tone explicitly
    stopActiveTones(true);
    
    // Reset chakra frequency tracking
    previousChakraFrequencyRef.current = null;
    
    // End meditation session
    if (currentSession) {
      endSession();
    }
    
    // Clear meditation state
    setSelectedMeditation(null);
    setCurrentStepIndex(0);
    setTimeRemaining(0);
    
    // Clear all timers
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }
    if (backgroundToneTimerRef.current) {
      clearTimeout(backgroundToneTimerRef.current);
      backgroundToneTimerRef.current = null;
    }
  }, [stopAudio, currentSession, endSession, stopActiveTones]);

  /**
   * Complete meditation
   * Formula: Play completion bell → End session → Reset state
   * Completion bell: 528 Hz (Love frequency) for 2 seconds
   */
  const handleComplete = useCallback(() => {
    setIsPlaying(false);
    
    // Stop background audio
    stopAudio();
    stopActiveTones(true);
    
    // Reset chakra frequency tracking
    previousChakraFrequencyRef.current = null;
    
    // Play completion bell with explicit frequency calculation
    // 528 Hz = Solfeggio MI frequency (Love/Transformation/Completion)
    const completionBellFrequency = 528.0; // Hz
    const completionBellDuration = 2.0; // seconds
    
    if (isAudioInitialized) {
      playSacredTone(completionBellFrequency, completionBellDuration, 'sine');
    }
    
    // End meditation session
    if (currentSession) {
      endSession();
    }
    
    toast.success('✨ Meditation complete! Well done.');
    
    // Reset state after completion bell plays
    // Formula: Wait for bell duration (2s) + fade out time (0.5s) = 2.5s total
    const resetDelay = (completionBellDuration + 0.5) * 1000; // Convert to milliseconds
    setTimeout(() => {
      setSelectedMeditation(null);
      setCurrentStepIndex(0);
      setTimeRemaining(0);
    }, resetDelay);
  }, [stopAudio, isAudioInitialized, playSacredTone, currentSession, endSession, stopActiveTones]);

  /**
   * Skip to next step
   * Formula: If more steps exist, increment index; otherwise complete
   */
  const handleSkip = useCallback(() => {
    if (!selectedMeditation) return;
    
    const totalSteps = selectedMeditation.steps.length;
    const currentIndex = currentStepIndex;
    
    // Formula: If currentIndex < totalSteps - 1, there are more steps
    if (currentIndex < totalSteps - 1) {
      // Move to next step: increment index by 1
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // All steps complete
      handleComplete();
    }
  }, [selectedMeditation, currentStepIndex, handleComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all timers
      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
      }
      if (backgroundToneTimerRef.current) {
        clearTimeout(backgroundToneTimerRef.current);
      }
      if (bellTimerRef.current) {
        clearTimeout(bellTimerRef.current);
      }
      // Stop any active meditation tones.
      stopActiveTones(true);
      // Reset chakra frequency tracking
      previousChakraFrequencyRef.current = null;
      // Stop all audio
      stopAudio();
    };
  }, [stopAudio, stopActiveTones]);

  const currentStep = selectedMeditation?.steps[currentStepIndex];
  const selectedVisual = selectedMeditation
    ? MEDITATION_VISUAL_STYLES[selectedMeditation.id] || DEFAULT_MEDITATION_VISUAL_STYLE
    : DEFAULT_MEDITATION_VISUAL_STYLE;
  const guidedVars = {
    '--guided-bg-1': '#08121d',
    '--guided-bg-2': '#102334',
    '--guided-text': '#e2e8f0',
    '--guided-muted': '#94a3b8'
  } as React.CSSProperties;

  return (
    <div style={{ ...guidedVars, maxWidth: '800px', margin: '0 auto', fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
      <h2 style={{
        fontSize: '1.7rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: '600',
        color: '#14b8a6',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        Guided Meditations
      </h2>

      {!selectedMeditation ? (
        // Meditation Selection
        <div style={{
          display: 'grid',
          gap: '1rem'
        }}>
          {guidedMeditations.map((meditation, index) => {
            const visual = MEDITATION_VISUAL_STYLES[meditation.id] || DEFAULT_MEDITATION_VISUAL_STYLE;
            return (
            <motion.div
              key={meditation.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleStart(meditation)}
              style={{
                background: `radial-gradient(circle at 85% 10%, ${visual.halo} 0%, transparent 40%), ${visual.gradient}, linear-gradient(145deg, var(--guided-bg-1) 0%, var(--guided-bg-2) 100%)`,
                borderRadius: visual.tileShape,
                padding: '1.5rem',
                border: `1px solid ${visual.accent}66`,
                boxShadow: `0 16px 30px ${visual.halo}`,
                cursor: 'pointer'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: visual.accent
                }}>
                  {meditation.title}
                </h3>
                <span style={{
                  fontSize: '0.875rem',
                  color: 'var(--guided-muted)',
                  fontFamily: "'Space Grotesk', 'Avenir Next', sans-serif"
                }}>
                  {meditation.duration} min
                </span>
              </div>
              
              <p style={{
                color: 'var(--guided-text)',
                fontSize: '1rem',
                lineHeight: '1.6'
              }}>
                {meditation.description}
              </p>
            </motion.div>
            );
          })}
        </div>
      ) : (
        // Meditation Player
        <div style={{
          background: `radial-gradient(circle at 90% 8%, ${selectedVisual.halo} 0%, transparent 35%), linear-gradient(145deg, var(--guided-bg-1) 0%, var(--guided-bg-2) 100%)`,
          borderRadius: '28px 18px 28px 18px',
          padding: '2rem',
          border: `1px solid ${selectedVisual.accent}66`,
          boxShadow: `0 20px 40px ${selectedVisual.halo}`
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: selectedVisual.accent,
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            {selectedMeditation.title}
          </h3>

          {currentStep && (
            <motion.div
              key={currentStepIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: `linear-gradient(145deg, ${selectedVisual.accent}24 0%, rgba(15, 23, 42, 0.38) 100%)`,
                borderRadius: '18px 12px 18px 12px',
                padding: '2rem',
                marginBottom: '2rem',
                minHeight: '150px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                border: `1px solid ${selectedVisual.accent}55`
              }}
            >
              <p style={{
                color: 'var(--guided-text)',
                fontSize: '1.125rem',
                lineHeight: '1.8',
                fontStyle: 'italic'
              }}>
                {currentStep.text}
              </p>
            </motion.div>
          )}

          {/* Progress */}
          <div style={{
            marginBottom: '2rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              color: 'var(--guided-muted)',
              fontFamily: "'Space Grotesk', 'Avenir Next', sans-serif"
            }}>
              <span>Step {currentStepIndex + 1} of {selectedMeditation.steps.length}</span>
              <span>{timeRemaining}s remaining</span>
            </div>
            
            <div style={{
              width: '100%',
              height: '8px',
              background: 'rgba(100, 116, 139, 0.2)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <motion.div
                style={{
                  height: '100%',
                  background: `linear-gradient(90deg, ${selectedVisual.accent}, #38bdf8)`,
                  borderRadius: '4px'
                }}
                initial={{ width: '0%' }}
                animate={{
                  width: `${((currentStepIndex + 1) / selectedMeditation.steps.length) * 100}%`
                }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Controls */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center'
          }}>
            <motion.button
              onClick={isPlaying ? handlePause : handleResume}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '12px 24px',
                background: `${selectedVisual.accent}28`,
                border: `1px solid ${selectedVisual.accent}`,
                borderRadius: '999px',
                color: '#e2e8f0',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontFamily: "'Space Grotesk', 'Avenir Next', sans-serif"
              }}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              {isPlaying ? 'Pause' : 'Resume'}
            </motion.button>

            <motion.button
              onClick={handleSkip}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '12px 24px',
                background: 'rgba(56, 189, 248, 0.24)',
                border: '1px solid #38bdf8',
                borderRadius: '999px',
                color: '#e0f2fe',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontFamily: "'Space Grotesk', 'Avenir Next', sans-serif"
              }}
            >
              <SkipForward size={20} />
              Skip
            </motion.button>

            <motion.button
              onClick={handleStop}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '12px 24px',
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid #ef4444',
                borderRadius: '999px',
                color: '#ef4444',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', 'Avenir Next', sans-serif"
              }}
            >
              Stop
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedMeditations;

